/**
 * Map V2 — Zoom thresholds, animation configs, and shared constants.
 *
 * These define the hierarchical discovery journey:
 * REGION → CLUSTER → LOCATION → PROJECT → DETAILS
 */

/* ── Zoom Level Bands ── */
export const ZOOM = {
  /** 4–6: Regions overview, no individual projects visible */
  REGION_MIN: 4,
  REGION_MAX: 6,

  /** 6–9: City-level clusters visible */
  CITY_MIN: 6,
  CITY_MAX: 9,

  /** 10–12: Dense project clusters, start revealing points */
  DENSE_MIN: 10,
  DENSE_MAX: 12,

  /** 13+: Individual projects fully visible, preview allowed */
  PROJECT_MIN: 13,
  PROJECT_MAX: 15,

  /** 15+: Street-level, district context */
  LOCAL_MIN: 15,

  /** Threshold below which project click only flies-to (no preview) */
  PREVIEW_THRESHOLD: 11,

  /** Target zoom when flying to a single project */
  PROJECT_FLY_TARGET: 13,

  /** Initial map zoom */
  INITIAL: 5.2,

  /** Overview zoom — the default resting zoom for the map */
  OVERVIEW: 5.0,

  /** Min/max allowed zoom */
  MIN: 3.5,
  MAX: 18,
};

/* ── Camera / Animation ── */
export const CAMERA = {
  /** Default flyTo duration (ms) */
  FLY_DURATION: 700,

  /** Level 0→1: Country → Region (slightly faster, snappy) */
  REGION_FLY_DURATION: 800,

  /** Level 1→2: Region → Cluster expansion (controlled, smooth) */
  CLUSTER_EASE_DURATION: 900,

  /** Region fitBounds duration (ms) */
  REGION_FIT_DURATION: 850,

  /** Level 2→3: Cluster → Project (slower, deliberate) */
  PROJECT_FOCUS_ZOOM: 14,
  PROJECT_FOCUS_DURATION: 1000,

  /** Custom easing: smooth deceleration (ease-out cubic) */
  EASE_OUT_CUBIC: (t) => 1 - Math.pow(1 - t, 3),

  /** Custom easing: smooth region-level transition (ease-in-out quart) */
  EASE_IN_OUT_QUART: (t) => t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2,

  /** Custom easing: gentle ease-out for project focus */
  EASE_OUT_QUINT: (t) => 1 - Math.pow(1 - t, 5),

  /** Easing curve name (MapLibre built-in) */
  EASING: 'ease-in-out',

  /** Optional pitch at high zoom (degrees) */
  PITCH_AT_PROJECT: 0,

  /** Offset when preview panel is open [x, y] pixels */
  PREVIEW_OFFSET: [-180, 0],

  /** FitBounds padding (px) */
  REGION_PADDING: { top: 80, bottom: 80, left: 80, right: 80 },
};

/* ── Clustering ── */
export const CLUSTER = {
  /** Pixel radius for clustering (small — only groups very close points) */
  RADIUS: 30,

  /** Max zoom at which clusters are generated */
  MAX_ZOOM: 15,

  /** Distance threshold (degrees) for "same location" overlap detection */
  OVERLAP_THRESHOLD: 0.0005,

  /** Spiderfy ring radius in degrees (~80m at equator) */
  SPIDERFY_OFFSET: 0.0008,
};

/* ── Saudi Arabia Center & Bounds ── */
export const SAUDI = {
  /** Visual centroid of Saudi landmass */
  CENTER: [44.55, 23.9],  // [lng, lat] — adjusted west/south for landmass weight
  /** Tight bounds around Saudi Arabia's actual territory */
  BOUNDS: [
    [36.4, 16.2],  // SW (Red Sea coast near Yemen)
    [55.7, 32.2],  // NE (Persian Gulf north)
  ],
  /** Wider bounds for maxBounds (allows some panning room) */
  MAX_BOUNDS: [
    [28.0, 10.0],  // SW
    [64.0, 38.0],  // NE
  ],
  /** Symmetric padding for fitBounds — upward offset (bottom > top) */
  FIT_PADDING: { top: 60, bottom: 120, left: 80, right: 80 },
};

/* ── Cinematic Entry ── */
export const CINEMATIC = {
  /** Starting zoom — very zoomed out for dramatic reveal */
  START_ZOOM: 2.5,
  /** Delay before fly-in starts (ms) */
  ENTRY_DELAY: 300,
  /** Duration of the fly-in animation (ms) */
  ENTRY_DURATION: 2800,
  /** Smooth cinematic easing (ease-in-out cubic) */
  EASE: (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
};

/* ── Click Priority (higher = processed first) ── */
export const CLICK_PRIORITY = {
  REGION_POLYGON: 4,
  CLUSTER: 3,
  OVERLAP_GROUP: 2,
  SINGLE_PROJECT: 1,
};
